/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useState } from 'react';
import { parse, stringify } from '@tock/query-string';
import sign from 'jwt-encode';

import { isLocalHostname } from 'utils/domainUtils';

import { FACEBOOK_APP_ID, GOOGLE_CLIENT_ID } from 'constants/social';
import type { HelloJSStatic } from 'hellojs';

import { appleAuthHelpers, useScript } from 'react-apple-signin-auth';
import { getSettings } from 'settings/AppSettings';
import { useMountEffect } from '@tock/hooks';

export type Network = 'google' | 'facebook' | 'apple';

// We are setting these value on the window to detect popup blocking and working around it.
// (window as any).lastFrom = null;
// (window as any).lastContinueTo = null;

interface AppleAuthResponse {
  network: 'apple';
  authorization: {
    // single use auth token
    code: string;
    // jwt containing user data
    id_token: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName: string;
      lastName: string;
    };
  };
}

function postMessage<T>(value: T) {
  window.postMessage(value, getSettings(__ENV__).CONSUMER_URL_BASE);
}

/**
 * React Native facebook login.
 */
function handleFacebookLoginReactNative() {
  window.ReactNativeWebView.postMessage('facebookLogin');
}

/**
 * React Native google login.
 */
function handleGoogleLoginReactNative() {
  window.ReactNativeWebView.postMessage('googleLogin');
}

/**
 * React Native apple login.
 */
function handleAppleLoginReactNative() {
  window.ReactNativeWebView.postMessage('appleLogin');
}

export function initHelloJS() {
  import('hellojs').then((hello) =>
    hello.default.init(
      {
        facebook: FACEBOOK_APP_ID,
        google: GOOGLE_CLIENT_ID,
      },
      {
        redirect_uri: `${
          isLocalHostname(window.location.host)
            ? `http://${window.location.host}`
            : `https://${window.location.hostname}`
        }/oauth-redir`,
      }
    )
  );
}

/**
 * Facebook/Google login on the web
 */
function loginHelloJS(
  hello: HelloJSStatic,
  networkName: Network,
  displayType = 'popup',
  redirectUri: string,
  applyAnalyticsReferrerFix: boolean
) {
  const loginOptions: Dictionary<string | boolean> = {
    display: displayType,
    scope: 'email',
    force: true,
  };
  const explicitFields =
    networkName === 'facebook' ? { fields: 'email,first_name,last_name,id' } : {};

  if (displayType === 'page') {
    loginOptions.page_uri = redirectUri;
  } else if (displayType === 'none') {
    const authResponse = hello.getAuthResponse(networkName);
    if (!authResponse) {
      const response = {
        error: {
          message: 'Authorization token was not saved',
          code: 'missing',
        },
      };
      postMessage({ reject: response });
      return;
    } else {
      return hello(networkName)
        .api('me', explicitFields)
        .then(function (profileData) {
          postMessage({ resolve: profileData, authResponse: authResponse });
        })
        .then(null, function () {
          const response = {
            error: {
              message: 'The browser blocked communication to ' + networkName,
              code: 'missing',
            },
          };
          postMessage({ reject: response });
        });
    }
  }

  // The technique for this is described at
  // https://medium.com/fatllama-engineering/excluding-facebook-and-google-social-sign-in-from-your-referrals-with-google-analytics-ff81588e519
  // -- we have adapted it to our use of hello.js for social login.
  const loginCallback = applyAnalyticsReferrerFix
    ? () => ga('set', 'referrer', getSettings(__ENV__).CONSUMER_URL_BASE)
    : undefined;

  return hello(networkName)
    .login(loginOptions, loginCallback)
    .then(function () {
      return hello(networkName).api('me', explicitFields);
    })
    .then(function (profileData) {
      postMessage({ resolve: profileData, authResponse: hello(networkName).getAuthResponse() });
    })
    .then(null, function (response) {
      postMessage({ reject: response });
    });
}

function loginApple(usePopup = true) {
  appleAuthHelpers.signIn({
    authOptions: {
      // the service identifier ID registered for
      // sign in with apple on developer.apple.com
      clientId: 'com.tocktix.exploretock',
      scope: 'email name',
      redirectURI: `https://${window.location.hostname}/oauth-redir`,
      usePopup: usePopup,
    },
    onSuccess: (response: AppleAuthResponse) => {
      response.network = 'apple';
      postMessage({ authResponse: response });
    },
    onError: (error: any) => {
      postMessage({
        reject: {
          error,
        },
      });
    },
  });
}

/**
 * Helper method to trigger the oauth login.
 *
 * @param {string} network - The name of the network to login to ('google' or 'facebook')
 * @param {string} from - Where the login is coming from
 * @param {string} continueTo - Where to redirect to
 * @param {boolean} applyAnalyticsReferrerFix - Whether to try to fix the referrer so it doesn't seem like,
 *   the user is coming from, e.g., facebook.com as an external referrer
 * @param {string} displayType - The hello.js login mechanism to use, defaults to 'popup' but can
 *   also be 'page' requiring no popups or 'none' when returning from a login page redirect.
 */
export function loginWith(
  network: Network,
  from: string,
  continueTo: string,
  applyAnalyticsReferrerFix: boolean,
  displayType = 'popup'
) {
  if (window.ReactNative) {
    if (network === 'facebook') {
      handleFacebookLoginReactNative();
    } else if (network === 'google') {
      handleGoogleLoginReactNative();
    } else if (network === 'apple') {
      handleAppleLoginReactNative();
    }
    return;
  }

  (window as any).lastFrom = from;
  (window as any).lastContinueTo = continueTo;
  const continueState = continueTo || '/';
  const query = parse(window.location.search);
  query.network = network;
  query.from = 'oauth-check';
  query.continue = continueState;
  const searchString = stringify(query);
  const redirectUri = `${window.location.origin}${window.location.pathname}/oauth-check?${searchString}`;

  if (network === 'apple') {
    loginApple(displayType === 'popup');
  } else {
    import('hellojs').then((hello) =>
      loginHelloJS(hello.default, network, displayType, redirectUri, applyAnalyticsReferrerFix)
    );
  }
}

interface Props {
  onLoginSuccess(network: Network, accessToken: string, profile: OAuthProfile): void;
  onLoginFailure(error?: any): void;
  onInitialized?(): void;
  applyAnalyticsReferrerFix: boolean;
}

export interface OAuthProfile {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const OAuth: FC<Props> = ({
  onLoginSuccess,
  onLoginFailure,
  onInitialized,
  applyAnalyticsReferrerFix,
}) => {
  const [didInitHelloJS, setDidInitHelloJS] = useState(false);

  const handleOAuth = useCallback(
    (event: MessageEvent) => {
      if (!event) {
        return;
      }

      if (event?.data?.receiver && onInitialized) {
        onInitialized();
      } else if (event?.data?.authResponse && event?.data?.resolve?.email) {
        const response = event.data.authResponse;
        const profileData = event.data.resolve;
        const profile: OAuthProfile = {
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          email: profileData.email,
        };
        onLoginSuccess(response.network, response.access_token, profile);
      } else if (event?.data?.authResponse && event?.data?.authResponse?.network === 'apple') {
        const response = event.data.authResponse as AppleAuthResponse;
        // NOTE that for other providers, the raw JWT from the client auth is passed to the API
        // whereas the Apple auth process works slightly differently. They require an
        // authorization code AND the id_token to complete auth on the server. In order to
        // keep the convention of sending a JWT to the server, we create one with code and
        // id_token which can be decoded and used on the server.
        const jwt = sign(response.authorization, '');

        const responseUser = getResponseUser(response);
        onLoginSuccess(response.network, jwt, {
          email: responseUser.email,
          ...(responseUser.name || {}),
        });
      } else if (event?.data?.reject?.error?.code === 'blocked' && event?.data?.reject?.network) {
        const network = event.data.reject.network;
        loginWith(
          network,
          (window as any).lastFrom,
          (window as any).lastContinueTo,
          applyAnalyticsReferrerFix,
          'page'
        );
      } else if (event?.data?.reject?.error) {
        onLoginFailure(event.data.reject);
      }
    },
    [onInitialized, onLoginFailure, onLoginSuccess, applyAnalyticsReferrerFix]
  );

  // apple only returns the user data the very first
  // time a user authenticates with this app
  // but we can get the email from the id_token
  // if the response doesn't include the user data
  const getResponseUser = (
    response: AppleAuthResponse
  ): { email?: string; name?: { firstName: string; lastName: string } } => {
    if (response.user) {
      return response.user;
    }
    const payloadJson = atob(response.authorization.id_token.split('.')[1]);
    return {
      email: JSON.parse(payloadJson).email,
    };
  };

  useMountEffect(() => {
    if (!didInitHelloJS) {
      initHelloJS();
      setDidInitHelloJS(true);
    }

    window.addEventListener('message', handleOAuth);

    return function cleanup() {
      window.removeEventListener('message', handleOAuth);
    };
  });

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  return null;
};

export default OAuth;
